import React, { useState, useEffect } from 'react';
import '../App.css';

export interface LLMPricingRow {
  id: number;
  provider: string;
  model_id: string;
  region: string | null;
  input_per_1k_usd: number;
  output_per_1k_usd: number;
  source: string;
  created_at: string;
  updated_at: string | null;
}

interface LLMPricingProps {
  apiUrl: (path: string) => string;
  getAuthHeaders: (payload?: any, method?: string) => Record<string, string>;
  safeFetch?: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

const LLMPricing: React.FC<LLMPricingProps> = ({
  apiUrl,
  getAuthHeaders,
  safeFetch = fetch,
}) => {
  const [rows, setRows] = useState<LLMPricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editModal, setEditModal] = useState<{ open: boolean; row: LLMPricingRow | null }>({ open: false, row: null });
  const [editForm, setEditForm] = useState({ input_per_1k_usd: '', output_per_1k_usd: '', source: '' });
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    provider: '',
    model_id: '',
    region: '',
    input_per_1k_usd: '',
    output_per_1k_usd: '',
    source: 'manual',
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; row: LLMPricingRow | null }>({ open: false, row: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await safeFetch(apiUrl('/api/v1/admin/llm-pricing'), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load LLM pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openEdit = (row: LLMPricingRow) => {
    setEditModal({ open: true, row });
    setEditForm({
      input_per_1k_usd: String(row.input_per_1k_usd),
      output_per_1k_usd: String(row.output_per_1k_usd),
      source: row.source,
    });
  };

  const closeEdit = () => {
    setEditModal({ open: false, row: null });
    setEditForm({ input_per_1k_usd: '', output_per_1k_usd: '', source: '' });
  };

  const openCreate = () => {
    setCreateForm({
      provider: '',
      model_id: '',
      region: '',
      input_per_1k_usd: '',
      output_per_1k_usd: '',
      source: 'manual',
    });
    setError(null);
    setCreateModal(true);
  };

  const closeCreate = () => setCreateModal(false);

  const handleCreate = async () => {
    const provider = createForm.provider.trim();
    const model_id = createForm.model_id.trim();
    if (!provider || !model_id) {
      setError('Provider and Model ID are required');
      return;
    }
    const input = parseFloat(createForm.input_per_1k_usd);
    const output = parseFloat(createForm.output_per_1k_usd);
    if (isNaN(input) || input < 0 || isNaN(output) || output < 0) {
      setError('Input and output per 1K USD must be non-negative numbers');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        provider,
        model_id,
        region: createForm.region.trim() || null,
        input_per_1k_usd: input,
        output_per_1k_usd: output,
        source: createForm.source.trim() || 'manual',
      };
      const res = await safeFetch(apiUrl('/api/v1/admin/llm-pricing'), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.json().catch(() => ({}));
        throw new Error(Array.isArray(t.detail) ? t.detail[0]?.msg : t.detail || `HTTP ${res.status}`);
      }
      setSuccess('Pricing row created. Cache invalidated.');
      closeCreate();
      fetchRows();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (row: LLMPricingRow) => setDeleteConfirm({ open: true, row });
  const closeDeleteConfirm = () => setDeleteConfirm({ open: false, row: null });

  const handleDelete = async () => {
    if (!deleteConfirm.row) return;
    const id = deleteConfirm.row.id;
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await safeFetch(apiUrl(`/api/v1/admin/llm-pricing/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      setSuccess('Pricing row deleted. Cache invalidated.');
      closeDeleteConfirm();
      fetchRows();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editModal.row) return;
    const input = parseFloat(editForm.input_per_1k_usd);
    const output = parseFloat(editForm.output_per_1k_usd);
    if (isNaN(input) || input < 0 || isNaN(output) || output < 0) {
      setError('Input and output per 1k USD must be non-negative numbers');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        input_per_1k_usd: input,
        output_per_1k_usd: output,
        source: editForm.source || 'manual',
      };
      const res = await safeFetch(apiUrl(`/api/v1/admin/llm-pricing/${editModal.row.id}`), {
        method: 'PATCH',
        headers: getAuthHeaders(payload, 'PATCH'),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.json().catch(() => ({}));
        throw new Error(t.detail || `HTTP ${res.status}`);
      }
      setSuccess('Pricing updated. Cache invalidated.');
      closeEdit();
      fetchRows();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="llm-pricing-page" style={{ padding: 24 }}>
        <h1 style={{ marginBottom: 16 }}>LLM Model Pricing</h1>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="llm-pricing-page" style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 8 }}>LLM Model Pricing</h1>
      <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
        Cost per 1K tokens (input/output) by provider and model. Used for usage cost calculation. Edits invalidate the in-memory cache.
      </p>
      {error && <p style={{ color: '#c62828', marginBottom: 12 }}>{error}</p>}
      {success && <p style={{ color: '#2e7d32', marginBottom: 12 }}>{success}</p>}
      <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={openCreate}
          style={{ padding: '8px 16px', cursor: 'pointer', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 500 }}
        >
          Add new model
        </button>
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid #e6e8ea', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e6e8ea' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Provider</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Model ID</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Region</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Input/1K USD</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Output/1K USD</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Source</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                  No pricing rows. Run migration 221 or refresh from AWS (Bedrock).
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #e6e8ea' }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 13 }}>{row.provider}</td>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 13 }}>{row.model_id}</td>
                  <td style={{ padding: '10px 12px' }}>{row.region ?? '—'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{Number(row.input_per_1k_usd)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{Number(row.output_per_1k_usd)}</td>
                  <td style={{ padding: '10px 12px' }}>{row.source}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => openEdit(row)}
                      style={{ padding: '6px 12px', cursor: 'pointer', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4 }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => openDeleteConfirm(row)}
                      style={{ padding: '6px 12px', cursor: 'pointer', background: '#c62828', color: '#fff', border: 'none', borderRadius: 4 }}
                    >
                      Delete
                    </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editModal.open && editModal.row && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeEdit}
        >
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
              minWidth: 360,
              maxWidth: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: 16 }}>Edit pricing</h2>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              {editModal.row.provider} / {editModal.row.model_id}
              {editModal.row.region ? ` / ${editModal.row.region}` : ''}
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Input per 1K tokens (USD)</label>
              <input
                type="number"
                step="any"
                min="0"
                value={editForm.input_per_1k_usd}
                onChange={(e) => setEditForm((f) => ({ ...f, input_per_1k_usd: e.target.value }))}
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Output per 1K tokens (USD)</label>
              <input
                type="number"
                step="any"
                min="0"
                value={editForm.output_per_1k_usd}
                onChange={(e) => setEditForm((f) => ({ ...f, output_per_1k_usd: e.target.value }))}
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Source</label>
              <input
                type="text"
                value={editForm.source}
                onChange={(e) => setEditForm((f) => ({ ...f, source: e.target.value }))}
                placeholder="manual | aws_price_api"
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeEdit} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={saving}
                style={{ padding: '8px 16px', cursor: saving ? 'not-allowed' : 'pointer', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4 }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {createModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeCreate}
        >
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
              minWidth: 360,
              maxWidth: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: 16 }}>Add new LLM pricing</h2>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Provider *</label>
              <input
                type="text"
                value={createForm.provider}
                onChange={(e) => setCreateForm((f) => ({ ...f, provider: e.target.value }))}
                placeholder="e.g. openai, anthropic, bedrock"
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Model ID *</label>
              <input
                type="text"
                value={createForm.model_id}
                onChange={(e) => setCreateForm((f) => ({ ...f, model_id: e.target.value }))}
                placeholder="e.g. gpt-4o, claude-3-5-sonnet"
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Region (optional)</label>
              <input
                type="text"
                value={createForm.region}
                onChange={(e) => setCreateForm((f) => ({ ...f, region: e.target.value }))}
                placeholder="e.g. us-east-1 or leave blank"
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Input per 1K tokens (USD)</label>
              <input
                type="number"
                step="any"
                min="0"
                value={createForm.input_per_1k_usd}
                onChange={(e) => setCreateForm((f) => ({ ...f, input_per_1k_usd: e.target.value }))}
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Output per 1K tokens (USD)</label>
              <input
                type="number"
                step="any"
                min="0"
                value={createForm.output_per_1k_usd}
                onChange={(e) => setCreateForm((f) => ({ ...f, output_per_1k_usd: e.target.value }))}
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Source</label>
              <input
                type="text"
                value={createForm.source}
                onChange={(e) => setCreateForm((f) => ({ ...f, source: e.target.value }))}
                placeholder="manual | aws_price_api"
                style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeCreate} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={saving}
                style={{ padding: '8px 16px', cursor: saving ? 'not-allowed' : 'pointer', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4 }}
              >
                {saving ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm.open && deleteConfirm.row && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeDeleteConfirm}
        >
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
              minWidth: 320,
              maxWidth: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: 12 }}>Delete pricing row?</h2>
            <p style={{ marginBottom: 16, color: '#333' }}>
              {deleteConfirm.row.provider} / {deleteConfirm.row.model_id}
              {deleteConfirm.row.region ? ` (${deleteConfirm.row.region})` : ''}. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeDeleteConfirm} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                style={{ padding: '8px 16px', cursor: deleting ? 'not-allowed' : 'pointer', background: '#c62828', color: '#fff', border: 'none', borderRadius: 4 }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LLMPricing;
